import type { Campaign } from '../../../../../_ui-core_/mod-bonus/types';
import type { RouteGuardOutcome } from '../../router/router-types/route-guard-types';
import type { CornucopiaCashierDirection, CornucopiaClientIdentity } from '../cornucopia/cornucopia-client';
import type { TrackingEventSource } from '../tracking/tracking-service';

export type SSOData = {
  jwt: string;
};

export const Product = {
  PORTAL: 'PORTAL',
  SPORTS: 'SPORTS',
  GAMES: 'GAMES',
} as const;

export type Product = (typeof Product)[keyof typeof Product];

export type DepositNavigationOptions = {
  /**
   * The deposit bonus page hands off to the Cornucopia cashier with
   * `replaceState`, since a revisit would forward again. The cashier's exits then
   * go home. eBET ignores it.
   */
  readonly replaceHistory?: boolean;
};

export interface IProductService {
  /**
   * The running label's Cornucopia identifiers (`origin`, cashier-session
   * `channel`). Read by the cashier view from `window.$app.product`, so it holds
   * on a cold load of `/cashier/:direction` too. `undefined` for a label that has
   * no Cornucopia cashier.
   */
  readonly cornucopiaClient: CornucopiaClientIdentity | undefined;
  ssoLogin(): Promise<SSOData>;
  logout(): void;
  //----
  gotoAccount(): void;
  gotoDeposit(
    skipWelcomeOffer: boolean,
    source?: TrackingEventSource,
    campaign?: Campaign,
    options?: DepositNavigationOptions,
  ): void;
  gotoFtd(source: TrackingEventSource): void;
  gotoKyc(): void;
  gotoLogin(source?: string, redirectPath?: string): Promise<void>;
  gotoPersonalDetails(): void;
  gotoPromotions(source: TrackingEventSource): void;
  gotoSettings(): void;
  gotoDeactivateAndClosure(): void;
  gotoDepositLimit(): void;
  gotoLugasCbdLimit(source: string): void;
  gotoDepositLimitsHistory(): void;
  gotoLimitsHistory(): void;
  gotoLimitsSettings(): void;
  gotoSignup(source: TrackingEventSource | string): void;
  gotoVerification(): void;
  gotoPhoneVerification(): void;
  gotoWithdrawal(source?: TrackingEventSource): void;
  /**
   * Route guard of `/cashier/:direction`: the label's payment rules, the same ones
   * `gotoDeposit()` / `gotoWithdrawal()` check before navigating.
   */
  cashierGate(direction: CornucopiaCashierDirection): Promise<RouteGuardOutcome>;
  /**
   * eBET redirect for an allowed `/cashier/:direction` entry that Cornucopia does
   * not serve. Replaces the history entry. Only the cashier route calls this;
   * delete with the eBET flow.
   */
  gotoLegacyCashier(direction: CornucopiaCashierDirection): void;
  gotoMessages(): void;
  gotoSowQuestionnaire(): void;
  gotoRgQuestionnaire(): void;
  gotoTinkVerification(type: string): void;
  gotoHelpCenterOrContactUs(subPath?: string): void;
  setToken(token: string): IProductService;
  //----
  gotoProduct(product: Product, page: string): void;
}
